import type { GetLandingPageMappingsQuery } from '../graphql/generated/graphql'

export type LandingpageMappingRow = NonNullable<
    GetLandingPageMappingsQuery['landingpageMappings'][number]
>

function normalizeReferrerHostname(hostname: string): string {
    return hostname.trim().toLowerCase().replace(/^www\./, '')
}

/**
 * Strapi data matching the given SVID (case-insensitive). Used for the stele flow, where the mapping is
 * resolved from the `SVID` url parameter instead of the external referer host.
 */
export function findLandingMappingBySvid(
    svid: string,
    rows: LandingpageMappingRow[],
): LandingpageMappingRow | null {
    const needle = svid.trim().toLowerCase()
    if (!needle) {
        return null
    }

    for (const row of rows) {
        const rowSvid = row?.svid?.trim().toLowerCase()
        if (rowSvid && rowSvid === needle) {
            return row
        }
    }

    return null
}

function comparableFilialeStrings(filialeRaw: string): string[] {
    const raw = filialeRaw.trim()
    if (!raw) {
        return []
    }

    const out = new Set<string>()
    const lower = raw.toLowerCase()
    out.add(lower)

    try {
        const u = new URL(lower.includes('://') ? lower : `https://${lower}`)
        const host = u.hostname.replace(/^www\./, '').toLowerCase()
        out.add(host)
        out.add(`${host}${u.pathname}${u.search}`.replace(/\/$/, '') || host)
    } catch {
        out.add(lower.replace(/^www\./, ''))
    }

    return [...out].filter(Boolean)
}

/**
 * First Strapi row whose `LandingpageFiliale` contains the referer hostname,
 * or whose parsed host equals it (after normalizing `www`). If several match,
 * prefers the longest `LandingpageFiliale` (more specific).
 */
export function findLandingMappingForReferrerHost(
    referrerHostname: string,
    rows: LandingpageMappingRow[],
): LandingpageMappingRow | null {
    const host = normalizeReferrerHostname(referrerHostname)
    if (!host) {
        return null
    }

    const ranked: { row: LandingpageMappingRow; priority: number }[] = []

    for (const row of rows) {
        if (!row?.LandingpageFiliale) {
            continue
        }

        const needles = comparableFilialeStrings(row.LandingpageFiliale)
        let best = 0
        for (const needle of needles) {
            if (!needle) {
                continue
            }
            if (needle.includes(host)) {
                best = Math.max(best, 2)
            } else if (host.includes(needle) && needle.length >= 4) {
                best = Math.max(best, 1)
            } else if (needle === host) {
                best = Math.max(best, 3)
            }
        }

        if (best > 0) {
            ranked.push({
                row,
                priority: best * 1_000_000 + row.LandingpageFiliale.length,
            })
        }
    }

    if (ranked.length === 0) {
        return null
    }

    ranked.sort((a, b) => b.priority - a.priority)
    return ranked[0]!.row
}
